import { translate } from '../../../helpers/translations/translator'
import { CommentViewModel, EditShiftPlanningTimeFormData } from '../../../models'
import { Comment } from '../../entities/Comment/Comment'
import { postCommentApiCall } from '../../entities/Comment/CommentService'
import { isFetchResultSuccessful } from '../../entities/FetchResult'
import { NotificationCenterAction, NotificationCenterActionCategory, NotificationCenterCategory } from '../../entities/NotificationCenter/NotificationCenter'
import { endEventLoggerApiCall, startEventLoggerApiCall } from '../../entities/NotificationCenter/NotificationCenterService'
import { EditShiftPlanningFormData } from '../../entities/ShiftPlanning/ShiftPlanning'
import {
    deletePlannedUserCommentApiCall,
    deleteShiftPlanningApiCall,
    editShiftPlanningApiCall,
    postShiftPlanningApiCall,
} from '../../entities/ShiftPlanning/ShiftPlanningService'
import { ToastType } from '../../types'
import { TypedDispatch } from '../store'
import { setToast } from '../toasts/toastsReducer'
import {
    setError,
    setIsAddSuccessful,
    setIsDeleteSuccessful,
    setIsEditSuccessful,
    setIsLoading,
    setIsPlannedSuccessful,
    setLastAddedShiftPlanning,
    setLastDeletedShiftPlanningId,
    setShiftPlanning,
} from './shiftPlanningReducer'

export const editConceptShiftPlanningTime = (editShiftPlanningFormData: EditShiftPlanningTimeFormData, shiftPlanningId: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setIsEditSuccessful(false))
    dispatch(setError(''))

    try {
        const response = await editShiftPlanningApiCall(editShiftPlanningFormData, shiftPlanningId)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setShiftPlanning(response.data))
        dispatch(setIsEditSuccessful(true))
    } catch (error) {
        console.error('[editConceptShiftPlanningTime]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const editShiftPlanning = (editShiftPlanningFormData: EditShiftPlanningFormData, shiftPlanningId: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setIsEditSuccessful(false))
    dispatch(setError(''))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.SHIFT_PLANNING,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.SHIFT_PLANNING_UPDATED],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        if (editShiftPlanningFormData.comment) {
            await postCommentApiCall({
                comment: editShiftPlanningFormData.comment,
                relationshipId: shiftPlanningId,
                relationshipKey: 'shiftPlanning',
                relationshipType: 'shiftPlannings',
                eventLogTrackerData: responseTracker.data,
            })
        }

        const response = await editShiftPlanningApiCall(editShiftPlanningFormData, shiftPlanningId, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setShiftPlanning(response.data))
        dispatch(setIsEditSuccessful(true))
    } catch (error) {
        console.error('[editShiftPlanningTime]', error)
    } finally {
        dispatch(setIsLoading(false))
        await endEventLoggerApiCall(responseTracker.data)
    }
}

export const editShiftPlanningColor = (
    shiftPlanningId: string,
    color?: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setIsEditSuccessful(false))
    dispatch(setError(''))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.SHIFT_PLANNING,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.SHIFT_PLANNING_UPDATED],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        const response = await editShiftPlanningApiCall({ color: color || null }, shiftPlanningId, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setShiftPlanning(response.data))
        dispatch(setIsEditSuccessful(true))
    } catch (error) {
        console.error('[editShiftPlanningColor]', error)
    } finally {
        dispatch(setIsLoading(false))
        await endEventLoggerApiCall(responseTracker.data)
    }
}

export const planUnplannedShiftPlanning = (shiftPlanningId: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setIsPlannedSuccessful(false))
    dispatch(setError(''))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.SHIFT_PLANNING,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.SHIFT_PLANNING_REGISTRATION_ACCEPTED],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        const response = await editShiftPlanningApiCall({ planned: true }, shiftPlanningId, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setShiftPlanning(response.data))
        dispatch(setIsPlannedSuccessful(true))
    } catch (error) {
        console.error('[planUnplannedShiftPlanning]', error)
    } finally {
        dispatch(setIsLoading(false))
        await endEventLoggerApiCall(responseTracker.data)
    }
}

export const deleteShiftPlanning = (
    shiftPlanningId: string,
    isPlanned: boolean,
    comment?: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setIsDeleteSuccessful(false))
    dispatch(setError(''))

    const tags = ! isPlanned
        ? [NotificationCenterActionCategory.SHIFT_PLANNING_REGISTRATION_DELETED]
        : [NotificationCenterActionCategory.UNKNOWN]

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.SHIFT_PLANNING_REMOVAL,
        NotificationCenterAction.DELETED,
        tags,
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        const response = await deleteShiftPlanningApiCall(shiftPlanningId, comment || null, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setLastDeletedShiftPlanningId(shiftPlanningId))
        dispatch(setIsDeleteSuccessful(true))
    } catch (error) {
        console.error('[deleteShiftPlanning]', error)
    } finally {
        dispatch(setIsLoading(false))
        await endEventLoggerApiCall(responseTracker.data)
    }
}

export const addShiftPlanning = (shiftId: string, userId: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setIsAddSuccessful(false))
    dispatch(setError(''))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.SHIFT_PLANNING,
        NotificationCenterAction.CREATED,
        [NotificationCenterActionCategory.SHIFT_PLANNING_CREATED],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        const response = await postShiftPlanningApiCall(shiftId, userId, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setLastAddedShiftPlanning(response.data))
        dispatch(setIsAddSuccessful(true))
    } catch (error) {
        console.error('[addShiftPlanning]', error)
    } finally {
        dispatch(setIsLoading(false))
        await endEventLoggerApiCall(responseTracker.data)
    }
}

export const deletePlannedUserComment = (comment: CommentViewModel | Comment) => async (dispatch: TypedDispatch): Promise<void> => {
    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.SHIFT_PLANNING,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.SHIFT_PLANNING_COMMENT_DELETED],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error));
        return
    }

    try {
        const response = await deletePlannedUserCommentApiCall(comment.id, responseTracker.data);

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
        }

        dispatch(setToast({
            text: translate('pages.shifts.deleteCommentSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[deletePlannedUserComment]', error)
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
    }
}
